import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Calculator,
  Search,
  TrendingUp,
  Building2,
  DollarSign,
  Percent,
  Award,
  Trophy,
  Target,
  Sparkles,
  RefreshCw,
  Download,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Star,
  Loader2,
  Banknote,
  Users,
  Building,
  ChevronRight
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Simulador() {
  const [loading, setLoading] = useState(false);
  
  // Obter perfil do usuário logado para controle de visibilidade de valores
  const userStr = localStorage.getItem('usuario');
  const user = userStr ? JSON.parse(userStr) : {};
  const userPerfil = user.perfil || '';
  
  // Verificar permissões de visualização de valores de comissões
  const isAdmin = ['MASTER', 'MASTER_GERAL', 'ADMIN', 'ADMIN_EMPRESA', 'GERENTE'].includes(userPerfil);
  const isAtendente = userPerfil === 'ATENDENTE';
  const isCorretor = userPerfil === 'CORRETOR';
  
  // Definir quais valores de comissão o usuário pode ver
  const canSeeValorEmpresa = isAdmin;
  const canSeeValorAtendente = isAdmin || isAtendente;
  const canSeeValorCorretor = isAdmin || isCorretor;
  
  const [instituicoes, setInstituicoes] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [bancosDisponiveis, setBancosDisponiveis] = useState([]);
  const [tiposOperacaoDisponiveis, setTiposOperacaoDisponiveis] = useState([]);
  const [tabelas, setTabelas] = useState([]);
  const [resultados, setResultados] = useState([]);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    instituicao: "",
    banco: "",
    tipoOperacao: "",
    margem: "",
    valorSolicitado: "",
    prazoMinimo: "",
    prazoMaximo: ""
  });

  // Melhores opções
  const [melhoresOpcoes, setMelhoresOpcoes] = useState({
    melhorEmpresa: null,
    melhorAtendente: null,
    melhorCorretor: null,
    melhorCliente: null
  });

  useEffect(() => {
    carregarDados();
  }, []);

  // Atualizar bancos e tipos de operação disponíveis quando instituição/banco mudar
  useEffect(() => {
    if (filtros.instituicao) {
      const instCodigo = filtros.instituicao.split(' - ')[0];
      
      // Filtrar tabelas pela instituição selecionada
      const tabelasDaInstituicao = tabelas.filter(tabela => 
        tabela.instituicao && tabela.instituicao.includes(instCodigo)
      );
      
      // Extrair bancos únicos
      const bancosComTabela = new Map();
      tabelasDaInstituicao.forEach(tabela => {
        if (tabela.banco_id) {
          bancosComTabela.set(tabela.banco_id, true);
        }
      });
      const bancosFiltrados = bancos.filter(b => bancosComTabela.has(b.id));
      setBancosDisponiveis(bancosFiltrados);
      
      // Filtrar tabelas pelo banco se selecionado
      let tabelasParaTipos = tabelasDaInstituicao;
      if (filtros.banco) {
        tabelasParaTipos = tabelasDaInstituicao.filter(t => t.banco_id === filtros.banco);
      }
      
      // Extrair tipos de operação únicos das tabelas filtradas
      const tiposUnicos = new Set();
      tabelasParaTipos.forEach(tabela => {
        if (tabela.tipo_operacao) {
          tiposUnicos.add(tabela.tipo_operacao);
        }
      });
      setTiposOperacaoDisponiveis(Array.from(tiposUnicos).sort());
      
    } else {
      setBancosDisponiveis([]);
      setTiposOperacaoDisponiveis([]);
    }
    // Limpar banco e tipo quando mudar instituição
    if (!filtros.instituicao) {
      setFiltros(prev => ({ ...prev, banco: "", tipoOperacao: "" }));
    }
  }, [filtros.instituicao, filtros.banco, tabelas, bancos]);

  const carregarDados = async () => {
    try {
      const [instRes, bancosRes, tabelasRes] = await Promise.all([
        axios.get(`${API}/api/lookup/instituicoes`),
        axios.get(`${API}/api/bancos`),
        axios.get(`${API}/api/tabelas-comissao`)
      ]);
      
      const todasInstituicoes = instRes.data.instituicoes || instRes.data || [];
      const todosBancos = bancosRes.data || [];
      const todasTabelas = tabelasRes.data.tabelas || tabelasRes.data || [];
      
      // Filtrar apenas instituições que têm tabelas de comissão cadastradas
      // OU que têm parâmetros de operação nos bancos
      const instituicoesComTabela = new Set();
      
      // 1. Instituições com tabelas de comissão
      todasTabelas.forEach(tabela => {
        if (tabela.instituicao) {
          instituicoesComTabela.add(tabela.instituicao);
          const codigoMatch = tabela.instituicao.match(/^(\d+)/);
          if (codigoMatch) {
            instituicoesComTabela.add(codigoMatch[1]);
          }
        }
      });
      
      // 2. Instituições com parâmetros de operação nos bancos
      todosBancos.forEach(banco => {
        if (banco.parametros && banco.parametros.length > 0) {
          banco.parametros.forEach(param => {
            if (param.instituicao) {
              instituicoesComTabela.add(param.instituicao);
              const codigoMatch = param.instituicao.match(/^(\d+)/);
              if (codigoMatch) {
                instituicoesComTabela.add(codigoMatch[1]);
              }
            }
          });
        }
      });
      
      // Filtrar instituições - mostrar APENAS as que têm tabelas OU parâmetros
      const instituicoesFiltradas = todasInstituicoes.filter(inst => {
        if (typeof inst === 'string') {
          // Verificar correspondência exata
          if (instituicoesComTabela.has(inst)) return true;
          
          // Verificar pelo código EXATO (início da string seguido de espaço ou hífen)
          const codigoMatch = inst.match(/^(\d+)(\s|-)/);
          if (codigoMatch) {
            const codigoInst = codigoMatch[1];
            // Verificar se o código EXATO está no set
            if (instituicoesComTabela.has(codigoInst)) return true;
            
            // Verificar se alguma instituição do set tem o mesmo código EXATO
            for (const tabInst of instituicoesComTabela) {
              const tabCodigoMatch = tabInst.match(/^(\d+)(\s|-)/);
              if (tabCodigoMatch && tabCodigoMatch[1] === codigoInst) return true;
            }
          }
        }
        return false;
      });
      
      // IMPORTANTE: Mostrar APENAS instituições filtradas, não todas
      // Se não houver nenhuma instituição com tabela/parâmetros, mostrar vazio
      setInstituicoes(instituicoesFiltradas);
      setBancos(todosBancos);
      setTabelas(todasTabelas);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    }
  };

  const simular = async () => {
    if (!filtros.instituicao) {
      toast.error("Selecione uma instituição");
      return;
    }
    if (!filtros.margem && !filtros.valorSolicitado) {
      toast.error("Informe a margem ou valor solicitado");
      return;
    }

    setLoading(true);
    try {
      // Filtrar tabelas localmente (já carregadas)
      let tabelasFiltradas = tabelas.filter(tabela => {
        // Filtrar por instituição
        if (filtros.instituicao) {
          const instCodigo = filtros.instituicao.split(' - ')[0];
          if (!tabela.instituicao || !tabela.instituicao.includes(instCodigo)) {
            return false;
          }
        }
        // Filtrar por banco (se selecionado)
        if (filtros.banco && tabela.banco_id !== filtros.banco) {
          return false;
        }
        // Filtrar por tipo de operação (se selecionado)
        if (filtros.tipoOperacao && filtros.tipoOperacao !== "all" && tabela.tipo_operacao !== filtros.tipoOperacao) {
          return false;
        }
        return true;
      });
      
      // Calcular valores para cada tabela e suas comissões
      const margem = parseFloat(filtros.margem?.replace(",", ".") || 0);
      const valorSolicitado = parseFloat(filtros.valorSolicitado?.replace(",", ".") || 0);
      
      const resultadosCalculados = [];
      
      tabelasFiltradas.forEach(tabela => {
        const banco = bancos.find(b => b.id === tabela.banco_id);
        const bancoNome = banco?.nome || banco?.codigo || tabela.banco_id || "N/A";
        const comissoes = tabela.comissoes || [];
        
        // Se não tem comissões, criar uma entrada com dados da tabela
        if (comissoes.length === 0) {
          const fator = parseFloat(tabela.fator || 0);
          const prazo = parseInt(tabela.prazo || 96);
          const comissaoEmpresa = parseFloat(tabela.comissao_empresa || tabela.empresa || 0);
          const comissaoCorretor = parseFloat(tabela.comissao_corretor || tabela.corretor || 0);
          const comissaoAtendente = parseFloat(tabela.comissao_atendente || tabela.atendente || 0);
          
          let valorParcela = 0;
          let valorLiberado = 0;
          
          if (margem > 0 && fator > 0) {
            valorParcela = margem;
            valorLiberado = margem / fator;
          } else if (valorSolicitado > 0 && fator > 0) {
            valorLiberado = valorSolicitado;
            valorParcela = valorSolicitado * fator;
          }
          
          if (valorLiberado > 0) {
            // Comissão empresa é sobre o valor liberado
            const comissaoEmpresaValor = valorLiberado * (comissaoEmpresa / 100);
            // Comissão atendente e corretor são sobre a comissão da empresa
            const comissaoAtendenteValor = comissaoEmpresaValor * (comissaoAtendente / 100);
            const comissaoCorretorValor = comissaoEmpresaValor * (comissaoCorretor / 100);
            
            resultadosCalculados.push({
              id: tabela.id,
              banco_nome: bancoNome,
              banco_id: tabela.banco_id,
              tipo_operacao: tabela.tipo_operacao,
              correspondente: tabela.correspondente || "",
              produto: tabela.nome_tabela || tabela.nome || "Tabela Padrão",
              nome_tabela: tabela.nome_tabela || tabela.nome || "",
              prazo,
              fator,
              taxa: tabela.taxa || 0,
              valorParcela,
              valorLiberado,
              comissaoEmpresaPercent: comissaoEmpresa,
              comissaoCorretorPercent: comissaoCorretor,
              comissaoAtendentePercent: comissaoAtendente,
              comissaoEmpresaValor,
              comissaoCorretorValor,
              comissaoAtendenteValor
            });
          }
        } else {
          // Processar cada comissão
          comissoes.forEach(comissao => {
            const fator = parseFloat(comissao.fator || 0);
            const prazo = parseInt(comissao.prazo || comissao.prazo_meses || 96);
            const taxa = parseFloat(comissao.taxa || comissao.taxa_mensal || 0);
            const comissaoEmpresa = parseFloat(comissao.empresa || comissao.empresa_percentual || 0);
            const comissaoCorretor = parseFloat(comissao.corretor || comissao.corretor_percentual || 0);
            const comissaoAtendente = parseFloat(comissao.atendente || comissao.atendente_percentual || 0);
            
            let valorParcela = 0;
            let valorLiberado = 0;
            
            // Calcular usando fator se disponível
            if (fator > 0) {
              if (margem > 0) {
                valorParcela = margem;
                valorLiberado = margem / fator;
              } else if (valorSolicitado > 0) {
                valorLiberado = valorSolicitado;
                valorParcela = valorSolicitado * fator;
              }
            } 
            // Se não tem fator mas tem taxa, calcular usando Price
            else if (taxa > 0 && prazo > 0) {
              const taxaDecimal = taxa / 100;
              if (margem > 0) {
                valorParcela = margem;
                // VL = PMT * [(1 - (1 + i)^-n) / i]
                const fatorCalculado = (1 - Math.pow(1 + taxaDecimal, -prazo)) / taxaDecimal;
                valorLiberado = margem * fatorCalculado;
              } else if (valorSolicitado > 0) {
                valorLiberado = valorSolicitado;
                // PMT = VL * [i / (1 - (1 + i)^-n)]
                const fatorCalculado = taxaDecimal / (1 - Math.pow(1 + taxaDecimal, -prazo));
                valorParcela = valorSolicitado * fatorCalculado;
              }
            }
            // Se tem apenas comissões mas não fator/taxa, mostrar com valores zerados
            else if (comissaoEmpresa > 0 || comissaoCorretor > 0) {
              if (margem > 0) {
                valorParcela = margem;
                valorLiberado = margem * prazo * 0.8; // Estimativa: 80% do valor total
              } else if (valorSolicitado > 0) {
                valorLiberado = valorSolicitado;
                valorParcela = valorSolicitado / prazo / 0.8;
              }
            }
            
            // Sempre incluir se tem comissões definidas
            if (comissaoEmpresa > 0 || comissaoCorretor > 0 || valorLiberado > 0) {
              // Comissão empresa é sobre o valor liberado
              const comissaoEmpresaValor = valorLiberado > 0 ? valorLiberado * (comissaoEmpresa / 100) : 0;
              // Comissão atendente e corretor são sobre a comissão da empresa
              const comissaoAtendenteValor = comissaoEmpresaValor * (comissaoAtendente / 100);
              const comissaoCorretorValor = comissaoEmpresaValor * (comissaoCorretor / 100);
              
              resultadosCalculados.push({
                id: `${tabela.id}-${comissao.id || prazo}`,
                banco_nome: bancoNome,
                banco_id: tabela.banco_id,
                tipo_operacao: tabela.tipo_operacao,
                correspondente: tabela.correspondente || "",
                produto: comissao.produto || tabela.nome_tabela || tabela.nome || "Padrão",
                nome_tabela: tabela.nome_tabela || tabela.nome || "",
                prazo,
                fator: fator || null,
                taxa: taxa || null,
                valorParcela: valorParcela || 0,
                valorLiberado: valorLiberado || 0,
                comissaoEmpresaPercent: comissaoEmpresa,
                comissaoCorretorPercent: comissaoCorretor,
                comissaoAtendentePercent: comissaoAtendente,
                comissaoEmpresaValor,
                comissaoCorretorValor,
                comissaoAtendenteValor
              });
            }
          });
        }
      });
      
      // Filtrar por prazo se especificado
      const prazoMinimo = parseInt(filtros.prazoMinimo) || 0;
      const prazoMaximo = parseInt(filtros.prazoMaximo) || Infinity;
      
      const resultadosFiltradosPorPrazo = resultadosCalculados.filter(r => {
        if (prazoMinimo > 0 && r.prazo < prazoMinimo) return false;
        if (prazoMaximo < Infinity && r.prazo > prazoMaximo) return false;
        return true;
      });
      
      // Ordenar por valor liberado (maior primeiro)
      resultadosFiltradosPorPrazo.sort((a, b) => b.valorLiberado - a.valorLiberado);
      
      setResultados(resultadosFiltradosPorPrazo);
      
      // Encontrar melhores opções
      if (resultadosFiltradosPorPrazo.length > 0) {
        const melhorEmpresa = [...resultadosFiltradosPorPrazo].sort((a, b) => b.comissaoEmpresaValor - a.comissaoEmpresaValor)[0];
        const melhorAtendente = [...resultadosFiltradosPorPrazo].sort((a, b) => b.comissaoAtendenteValor - a.comissaoAtendenteValor)[0];
        const melhorCorretor = [...resultadosFiltradosPorPrazo].sort((a, b) => b.comissaoCorretorValor - a.comissaoCorretorValor)[0];
        const melhorCliente = [...resultadosFiltradosPorPrazo].sort((a, b) => b.valorLiberado - a.valorLiberado)[0];
        
        setMelhoresOpcoes({
          melhorEmpresa,
          melhorAtendente,
          melhorCorretor,
          melhorCliente
        });
      } else {
        setMelhoresOpcoes({
          melhorEmpresa: null,
          melhorAtendente: null,
          melhorCorretor: null,
          melhorCliente: null
        });
      }
      
      toast.success(`${resultadosFiltradosPorPrazo.length} opções encontradas`);
    } catch (error) {
      console.error("Erro ao simular:", error);
      toast.error("Erro ao realizar simulação");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const limparFiltros = () => {
    setFiltros({
      instituicao: "",
      banco: "",
      tipoOperacao: "",
      margem: "",
      valorSolicitado: "",
      prazoMinimo: "",
      prazoMaximo: ""
    });
    setResultados([]);
    setBancosDisponiveis([]);
    setMelhoresOpcoes({
      melhorEmpresa: null,
      melhorAtendente: null,
      melhorCorretor: null,
      melhorCliente: null
    });
  };

  return (
    <div className="space-y-6" data-testid="simulador-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Calculator className="h-7 w-7 text-indigo-600" />
            Simulador de Operações
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Compare as melhores opções por instituição, banco e tipo de operação
          </p>
        </div>
        <Button onClick={limparFiltros} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Limpar
        </Button>
      </div>

      {/* Filtros */}
      <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2 text-indigo-800">
            <Filter className="h-5 w-5" />
            Parâmetros da Simulação
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Instituição */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Instituição *</Label>
              <Select 
                value={filtros.instituicao} 
                onValueChange={(v) => setFiltros({...filtros, instituicao: v, banco: ""})}
              >
                <SelectTrigger data-testid="select-instituicao">
                  <SelectValue placeholder="Selecione a instituição" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {instituicoes.map((inst, idx) => (
                    <SelectItem key={idx} value={inst}>{inst}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Banco */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Banco</Label>
              <Select 
                value={filtros.banco} 
                onValueChange={(v) => setFiltros({...filtros, banco: v === "all" ? "" : v})}
                disabled={!filtros.instituicao}
              >
                <SelectTrigger data-testid="select-banco">
                  <SelectValue placeholder={filtros.instituicao ? "Todos os bancos" : "Selecione instituição"} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="all">Todos os bancos</SelectItem>
                  {bancosDisponiveis.map((banco) => (
                    <SelectItem key={banco.id} value={banco.id}>
                      {banco.codigo ? `${banco.codigo} - ${banco.nome}` : banco.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filtros.instituicao && bancosDisponiveis.length > 0 && (
                <p className="text-xs text-indigo-600">
                  {bancosDisponiveis.length} banco(s) disponível(is)
                </p>
              )}
            </div>

            {/* Tipo de Operação */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Tipo de Operação</Label>
              <Select 
                value={filtros.tipoOperacao} 
                onValueChange={(v) => setFiltros({...filtros, tipoOperacao: v === "all" ? "" : v})}
                disabled={!filtros.instituicao}
              >
                <SelectTrigger data-testid="select-tipo-operacao">
                  <SelectValue placeholder={filtros.instituicao ? "Todos os tipos" : "Selecione instituição"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {tiposOperacaoDisponiveis.map(tipo => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filtros.instituicao && tiposOperacaoDisponiveis.length > 0 && (
                <p className="text-xs text-indigo-600">
                  {tiposOperacaoDisponiveis.length} tipo(s) disponível(is)
                </p>
              )}
            </div>

            {/* Margem */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Margem Disponível (R$)</Label>
              <Input
                placeholder="0,00"
                value={filtros.margem}
                onChange={(e) => setFiltros({...filtros, margem: e.target.value})}
                data-testid="input-margem"
              />
            </div>

            {/* Prazo */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Prazo Máximo (meses)</Label>
              <Input
                placeholder="Ex: 48, 60, 84..."
                value={filtros.prazoMaximo}
                onChange={(e) => setFiltros({...filtros, prazoMaximo: e.target.value})}
                data-testid="input-prazo-maximo"
                type="number"
              />
            </div>

            {/* Valor Solicitado */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Valor Solicitado (R$)</Label>
              <Input
                placeholder="0,00"
                value={filtros.valorSolicitado}
                onChange={(e) => setFiltros({...filtros, valorSolicitado: e.target.value})}
                data-testid="input-valor-solicitado"
              />
            </div>
          </div>

          {/* Botão Simular */}
          <div className="mt-6 flex justify-center">
            <Button 
              onClick={simular} 
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700 px-8 py-3 text-lg"
              data-testid="btn-simular"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Simulando...
                </>
              ) : (
                <>
                  <Search className="h-5 w-5 mr-2" />
                  Buscar Melhores Opções
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Melhores Opções - Cards */}
      {(melhoresOpcoes.melhorEmpresa || melhoresOpcoes.melhorAtendente || melhoresOpcoes.melhorCorretor || melhoresOpcoes.melhorCliente) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Melhor Comissão Empresa */}
          {melhoresOpcoes.melhorEmpresa && (
            <Card className="border-2 border-green-300 bg-gradient-to-br from-green-50 to-green-100 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Badge className="bg-green-500 text-white">
                    <Trophy className="h-3 w-3 mr-1" />
                    Melhor para Empresa
                  </Badge>
                  <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center">
                    <Building className="h-5 w-5 text-white" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="font-bold text-lg text-green-800">{melhoresOpcoes.melhorEmpresa.banco_nome || melhoresOpcoes.melhorEmpresa.banco}</p>
                  <p className="text-sm text-green-700">{melhoresOpcoes.melhorEmpresa.tipo_operacao || melhoresOpcoes.melhorEmpresa.produto}</p>
                  <div className="pt-2 border-t border-green-200">
                    <p className="text-2xl font-bold text-green-600">
                      {canSeeValorEmpresa ? formatCurrency(melhoresOpcoes.melhorEmpresa.comissaoEmpresaValor) : "---"}
                    </p>
                    <p className="text-xs text-green-600">
                      {canSeeValorEmpresa ? `${melhoresOpcoes.melhorEmpresa.comissaoEmpresaPercent}% de comissão` : "Valor oculto"}
                    </p>
                  </div>
                  <div className="text-xs text-green-700 space-y-1 pt-2">
                    <p>Prazo: {melhoresOpcoes.melhorEmpresa.prazo} meses</p>
                    <p>Liberado: {formatCurrency(melhoresOpcoes.melhorEmpresa.valorLiberado)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Melhor Comissão Atendente */}
          {melhoresOpcoes.melhorAtendente && (
            <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-purple-100 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Badge className="bg-purple-500 text-white">
                    <Award className="h-3 w-3 mr-1" />
                    Melhor para Atendente
                  </Badge>
                  <div className="h-10 w-10 rounded-full bg-purple-500 flex items-center justify-center">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="font-bold text-lg text-purple-800">{melhoresOpcoes.melhorAtendente.banco_nome || melhoresOpcoes.melhorAtendente.banco}</p>
                  <p className="text-sm text-purple-700">{melhoresOpcoes.melhorAtendente.tipo_operacao || melhoresOpcoes.melhorAtendente.produto}</p>
                  <div className="pt-2 border-t border-purple-200">
                    <p className="text-2xl font-bold text-purple-600">
                      {canSeeValorAtendente ? formatCurrency(melhoresOpcoes.melhorAtendente.comissaoAtendenteValor) : "---"}
                    </p>
                    <p className="text-xs text-purple-600">
                      {canSeeValorAtendente ? `${melhoresOpcoes.melhorAtendente.comissaoAtendentePercent}% de comissão` : "Valor oculto"}
                    </p>
                  </div>
                  <div className="text-xs text-purple-700 space-y-1 pt-2">
                    <p>Prazo: {melhoresOpcoes.melhorAtendente.prazo} meses</p>
                    <p>Liberado: {formatCurrency(melhoresOpcoes.melhorAtendente.valorLiberado)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Melhor Comissão Corretor */}
          {melhoresOpcoes.melhorCorretor && (
            <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Badge className="bg-amber-500 text-white">
                    <Award className="h-3 w-3 mr-1" />
                    Melhor para Corretor
                  </Badge>
                  <div className="h-10 w-10 rounded-full bg-amber-500 flex items-center justify-center">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="font-bold text-lg text-amber-800">{melhoresOpcoes.melhorCorretor.banco_nome || melhoresOpcoes.melhorCorretor.banco}</p>
                  <p className="text-sm text-amber-700">{melhoresOpcoes.melhorCorretor.tipo_operacao || melhoresOpcoes.melhorCorretor.produto}</p>
                  <div className="pt-2 border-t border-amber-200">
                    <p className="text-2xl font-bold text-amber-600">
                      {canSeeValorCorretor ? formatCurrency(melhoresOpcoes.melhorCorretor.comissaoCorretorValor) : "---"}
                    </p>
                    <p className="text-xs text-amber-600">
                      {canSeeValorCorretor ? `${melhoresOpcoes.melhorCorretor.comissaoCorretorPercent}% de comissão` : "Valor oculto"}
                    </p>
                  </div>
                  <div className="text-xs text-amber-700 space-y-1 pt-2">
                    <p>Prazo: {melhoresOpcoes.melhorCorretor.prazo} meses</p>
                    <p>Liberado: {formatCurrency(melhoresOpcoes.melhorCorretor.valorLiberado)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Melhor Valor Liberado Cliente */}
          {melhoresOpcoes.melhorCliente && (
            <Card className="border-2 border-blue-300 bg-gradient-to-br from-blue-50 to-blue-100 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Badge className="bg-blue-500 text-white">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Melhor para Cliente
                  </Badge>
                  <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                    <Banknote className="h-5 w-5 text-white" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="font-bold text-lg text-blue-800">{melhoresOpcoes.melhorCliente.banco_nome || melhoresOpcoes.melhorCliente.banco}</p>
                  <p className="text-sm text-blue-700">{melhoresOpcoes.melhorCliente.tipo_operacao || melhoresOpcoes.melhorCliente.produto}</p>
                  <div className="pt-2 border-t border-blue-200">
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(melhoresOpcoes.melhorCliente.valorLiberado)}
                    </p>
                    <p className="text-xs text-blue-600">
                      Valor liberado para o cliente
                    </p>
                  </div>
                  <div className="text-xs text-blue-700 space-y-1 pt-2">
                    <p>Prazo: {melhoresOpcoes.melhorCliente.prazo} meses</p>
                    <p>Parcela: {formatCurrency(melhoresOpcoes.melhorCliente.valorParcela)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Legenda */}
      {resultados.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center gap-6 justify-center text-sm">
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-full bg-green-500"></div>
                <span>Melhor Comissão para Empresa</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-full bg-purple-500"></div>
                <span>Melhor Comissão para Atendente</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-full bg-amber-500"></div>
                <span>Melhor Comissão para Corretor</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-full bg-blue-500"></div>
                <span>Melhor Valor Liberado para Cliente</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela de Resultados */}
      {resultados.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-indigo-600" />
                Resultados da Simulação ({resultados.length} opções)
              </CardTitle>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-[500px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-100 z-10">
                    <TableRow>
                      <TableHead className="font-bold">Banco</TableHead>
                      <TableHead className="font-bold">Tipo Operação</TableHead>
                      <TableHead className="font-bold">Correspondente</TableHead>
                      <TableHead className="text-center font-bold">Prazo</TableHead>
                      <TableHead className="text-center font-bold">Fator</TableHead>
                      <TableHead className="text-center font-bold">Taxa %</TableHead>
                      <TableHead className="text-right font-bold">Valor Liberado</TableHead>
                      <TableHead className="text-right font-bold">Valor Parcela</TableHead>
                      <TableHead className="text-center font-bold bg-green-50">Com. Empresa</TableHead>
                      <TableHead className="text-center font-bold bg-purple-50">Com. Atendente</TableHead>
                      <TableHead className="text-center font-bold bg-amber-50">Com. Corretor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      // Função helper para comparar resultados de forma robusta
                      const isSameResult = (melhor, atual) => {
                        if (!melhor || !atual) return false;
                        if (melhor.id && atual.id && melhor.id === atual.id) return true;
                        const mesmoBanco = (melhor.banco_nome || melhor.banco) === (atual.banco_nome || atual.banco);
                        const mesmoTipo = 
                          (melhor.tipo_operacao === atual.tipo_operacao) ||
                          (melhor.produto === atual.produto) ||
                          (melhor.tipo_operacao === atual.produto) ||
                          (melhor.produto === atual.tipo_operacao);
                        const mesmoPrazo = melhor.prazo === atual.prazo;
                        const mesmoValor = Math.abs((melhor.valorLiberado || 0) - (atual.valorLiberado || 0)) < 1;
                        const mesmaComissao = Math.abs((melhor.comissaoEmpresaValor || 0) - (atual.comissaoEmpresaValor || 0)) < 1;
                        return mesmoBanco && mesmoTipo && mesmoPrazo && mesmoValor && mesmaComissao;
                      };
                      
                      // Ordenar resultados: melhores opções primeiro
                      const resultadosOrdenados = [...resultados].sort((a, b) => {
                        const aIsMelhor = 
                          isSameResult(melhoresOpcoes.melhorEmpresa, a) ||
                          isSameResult(melhoresOpcoes.melhorAtendente, a) ||
                          isSameResult(melhoresOpcoes.melhorCorretor, a) ||
                          isSameResult(melhoresOpcoes.melhorCliente, a);
                        const bIsMelhor = 
                          isSameResult(melhoresOpcoes.melhorEmpresa, b) ||
                          isSameResult(melhoresOpcoes.melhorAtendente, b) ||
                          isSameResult(melhoresOpcoes.melhorCorretor, b) ||
                          isSameResult(melhoresOpcoes.melhorCliente, b);
                        
                        // Melhores opções vão para o topo
                        if (aIsMelhor && !bIsMelhor) return -1;
                        if (!aIsMelhor && bIsMelhor) return 1;
                        // Se ambos são melhores ou nenhum é, manter ordem por valor liberado
                        return (b.valorLiberado || 0) - (a.valorLiberado || 0);
                      });
                      
                      return resultadosOrdenados.map((resultado, idx) => {
                        const isMelhorEmpresa = isSameResult(melhoresOpcoes.melhorEmpresa, resultado);
                        const isMelhorAtendente = isSameResult(melhoresOpcoes.melhorAtendente, resultado);
                        const isMelhorCorretor = isSameResult(melhoresOpcoes.melhorCorretor, resultado);
                        const isMelhorCliente = isSameResult(melhoresOpcoes.melhorCliente, resultado);
                      
                      return (
                        <TableRow 
                          key={resultado.id || idx}
                          className={cn(
                            "hover:bg-slate-50",
                            isMelhorEmpresa && "bg-green-50",
                            isMelhorAtendente && "bg-purple-50",
                            isMelhorCorretor && "bg-amber-50",
                            isMelhorCliente && "bg-blue-50"
                          )}
                        >
                          <TableCell className="font-semibold">
                            <div className="flex items-center gap-2">
                              {isMelhorEmpresa && <div className="h-3 w-3 rounded-full bg-green-500" />}
                              {isMelhorAtendente && <div className="h-3 w-3 rounded-full bg-purple-500" />}
                              {isMelhorCorretor && <div className="h-3 w-3 rounded-full bg-amber-500" />}
                              {isMelhorCliente && <div className="h-3 w-3 rounded-full bg-blue-500" />}
                              {resultado.banco_nome || resultado.banco}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {resultado.tipo_operacao || resultado.produto || "-"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {resultado.correspondente || resultado.nome_tabela || "-"}
                          </TableCell>
                          <TableCell className="text-center">{resultado.prazo || "-"}</TableCell>
                          <TableCell className="text-center font-mono text-xs">
                            {resultado.fator ? parseFloat(resultado.fator).toFixed(8) : "-"}
                          </TableCell>
                          <TableCell className="text-center">
                            {resultado.taxa ? `${resultado.taxa}%` : "-"}
                          </TableCell>
                          <TableCell className="text-right font-bold text-blue-600">
                            {formatCurrency(resultado.valorLiberado)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(resultado.valorParcela)}
                          </TableCell>
                          <TableCell className="text-center bg-green-50/50">
                            <div>
                              <span className="font-bold text-green-700">
                                {canSeeValorEmpresa ? formatCurrency(resultado.comissaoEmpresaValor) : "---"}
                              </span>
                              <span className="text-xs text-green-600 block">
                                {canSeeValorEmpresa ? `(${resultado.comissaoEmpresaPercent}%)` : ""}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center bg-purple-50/50">
                            <div>
                              <span className="font-bold text-purple-700">
                                {canSeeValorAtendente ? formatCurrency(resultado.comissaoAtendenteValor) : "---"}
                              </span>
                              <span className="text-xs text-purple-600 block">
                                {canSeeValorAtendente ? `(${resultado.comissaoAtendentePercent}%)` : ""}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center bg-amber-50/50">
                            <div>
                              <span className="font-bold text-amber-700">
                                {canSeeValorCorretor ? formatCurrency(resultado.comissaoCorretorValor) : "---"}
                              </span>
                              <span className="text-xs text-amber-600 block">
                                {canSeeValorCorretor ? `(${resultado.comissaoCorretorPercent}%)` : ""}
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    });
                    })()}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Estado vazio */}
      {resultados.length === 0 && !loading && (
        <Card className="border-dashed border-2 border-slate-300">
          <CardContent className="py-16">
            <div className="text-center">
              <Calculator className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">Nenhuma simulação realizada</h3>
              <p className="text-slate-400 text-sm max-w-md mx-auto">
                Selecione uma instituição e informe a margem disponível ou valor solicitado para encontrar as melhores opções de operação.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
